import React, { useState, useEffect, useCallback } from 'react';
import { getAuth, signOut } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, getDoc, updateDoc, deleteDoc, arrayUnion, arrayRemove, increment, setDoc, onSnapshot } from 'firebase/firestore';
import { app, db} from '../../firebase/firebaseConfig';
import { Home, Mail, LogOut, Settings, SlidersHorizontal, Play, Power, Check, X, UserPlus, Crown, MessageCircle, LayoutGrid, Info, BookOpen } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import ErrorModal from '../ui/ErrorModal';
import SupportChat from '../Support/SupportChat';
import DocumentationViewer from '../Documentation/DocumentationViewer';
import { useSupportMessages } from '../Support/useSupportMessages';


// ─── Per-Family Configure Button Toggle ─────────────────────────────────────
// The per-family "Configure" button (in FamilyRow) navigates to the HomeDashboard
// for a specific family, allowing per-family Higlo/Igloo configuration (create/delete
// Higlos, manage members, rename, etc.). This functionality has been superseded by
// the account-level "Configure Higlos" button (ConfigureHiglos component), which
// provides the same capabilities in a unified matrix view.
//
// The per-family button is disabled by default but the backend code and handler
// (onManageFamily → handleManageFamily in App.js) are fully preserved. To re-enable
// the per-family Configure button, set this to `true`.
// ─────────────────────────────────────────────────────────────────────────────
const ENABLE_PER_FAMILY_CONFIGURE_BUTTON = false;

function IndividualDashboard({ onManageFamily, onSetupDevice, onDeviceSettings, onConfigureHiglos }) {
  const [userEmail, setUserEmail] = useState(null);
  const [accountUUID, setAccountUUID] = useState(null);
  const [primaryFamilyID, setPrimaryFamilyID] = useState(null);
  const [associatedFamilies, setAssociatedFamilies] = useState([]);
  const [owningFamilies, setOwningFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [familyInvites, setFamilyInvites] = useState([]);
  const [showSignoutModal, setShowSignoutModal] = useState(false);
  const [selectedFamilyForSignout, setSelectedFamilyForSignout] = useState(null);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSupportChat, setShowSupportChat] = useState(false);
  const [showDocs, setShowDocs] = useState(false);

  const [familiesWithSettings, setFamiliesWithSettings] = useState({});

  const { unreadCount, fetchUnreadCount } = useSupportMessages();

  const loadAssociatedFamilies = useCallback(async (uuid) => {
    try {
      // Use accountsInfo with UUID instead of individualInfo with email
      const accountDoc = await getDoc(doc(db, 'accountsInfo', uuid));
      if (accountDoc.exists()) {
        const data = accountDoc.data();
        const familyIDs = data.families || [];
        setAssociatedFamilies(familyIDs);

        // Check ownership for each family by querying the owners subcollection
        const ownedFamilyIDs = [];
        for (const familyID of familyIDs) {
          const ownerDoc = await getDoc(doc(db, 'familyInfo', familyID, 'owners', uuid));
          if (ownerDoc.exists()) {
            ownedFamilyIDs.push(familyID);
          }
        }
        setOwningFamilies(ownedFamilyIDs);
      }
    } catch (err) {
      console.error('Error fetching associated families:', err);
      setErrorMessage('Failed to fetch family information');
      setShowErrorModal(true);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const auth = getAuth(app);
    const db = getFirestore(app);

    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUserEmail(user.email);

        try {
          // Get the accountUUID from token claims
          const tokenResult = await user.getIdTokenResult();
          const uuid = tokenResult.claims.accountUUID;
          setAccountUUID(uuid);

          if (uuid) {
            // Get user data from accountsInfo using UUID
            const accountDoc = await getDoc(doc(db, 'accountsInfo', uuid));
            if (accountDoc.exists()) {
              setPrimaryFamilyID(accountDoc.data().primaryFamilyID);
            }

            // Load associated families
            await loadAssociatedFamilies(uuid);
          }
        } catch (err) {
          console.error('Error fetching data:', err);
          setErrorMessage('Failed to fetch family information');
          setShowErrorModal(true);
        }
      } else {
        setUserEmail(null);
        setAccountUUID(null);
        setPrimaryFamilyID(null);
        setAssociatedFamilies([]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [loadAssociatedFamilies]);

  const loadFamilyInvites = useCallback(async () => {
    const invitesList = [];
    const invitesCollectionRef = collection(db, "familyInviteMessages", userEmail, "familyIDs");
    try {
      const snapshot = await getDocs(invitesCollectionRef);
      snapshot.docs.forEach(docSnap => {
        invitesList.push({ familyID: docSnap.id });
      });
      setFamilyInvites(invitesList);
    } catch (error) {
      console.error("Error loading family invites:", error);
    }
  }, [userEmail]);

  useEffect(() => {
    if (userEmail) {
      loadFamilyInvites();
    }
  }, [userEmail, loadFamilyInvites]);

  // Check for openSupport query parameter (from email notification links)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('openSupport') === 'true') {
      setShowSupportChat(true);
      // Clean up the URL without refreshing the page
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const handleSignOut = async () => {
    const auth = getAuth(app);
    try {
      await signOut(auth).then(() => {
        window.location.reload();
      });
    } catch (err) {
      console.error('Error signing out:', err);
      setErrorMessage('Failed to sign out');
      setShowErrorModal(true);
    }
  };

  const handleMakePrimary = async (newPrimaryFamilyID) => {
    const db = getFirestore(app);
    try {
      // Update accountsInfo using accountUUID instead of individualInfo
      if (accountUUID) {
        await updateDoc(doc(db, 'accountsInfo', accountUUID), {
          primaryFamilyID: newPrimaryFamilyID
        });
      }
      setPrimaryFamilyID(newPrimaryFamilyID);
    } catch (err) {
      console.error('Error updating primary family:', err);
      setErrorMessage('Failed to update primary family');
      setShowErrorModal(true);
    }
  };

  const acceptFamilyInvite = async (familyID) => {
    const db = getFirestore(app);
    try {
      // Remove invite
      const inviteMessageRef = doc(db, "familyInviteMessages", userEmail, "familyIDs", familyID);
      await deleteDoc(inviteMessageRef);

      // Check if there are any remaining invites
      const remainingInvites = await getDocs(collection(db, "familyInviteMessages", userEmail, "familyIDs"));
      if (remainingInvites.empty) {
        // Delete the parent document if no more invites exist
        await deleteDoc(doc(db, "familyInviteMessages", userEmail));
      }

      // Remove from pending invites (user's email matches invitedUserID, so this is allowed)
      const pendingInvitesRef = doc(db, "familyInfo", familyID, "pendingControllerInvites", userEmail);
      await deleteDoc(pendingInvitesRef);

      if (accountUUID) {
        // IMPORTANT: Order matters here due to security rules
        // 1. First, add UUID-based controller (while email-based controller still exists, so isController passes)
        const uuidControllerRef = doc(db, "familyInfo", familyID, "controllers", accountUUID);
        await setDoc(uuidControllerRef, {});

        // TODO: Why would this already be there?
        // 2. Then, remove the email-based controller (now we have UUID-based, so isController still passes)
        const emailControllerRef = doc(db, "familyInfo", familyID, "controllers", userEmail);
        await deleteDoc(emailControllerRef).catch(() => {}); // Ignore if doesn't exist

        // 3. Finally, add to user's families array in accountsInfo (no invitedFamilies array anymore)
        const accountDocRef = doc(db, 'accountsInfo', accountUUID);
        await updateDoc(accountDocRef, {
          families: arrayUnion(familyID),
          numOfFamilies: increment(1),
          numOfInvitedFamilies: increment(1)
        });
      } else {
        // TODO: Does this branch make sense?
        // If no accountUUID, just remove email-based controller
        const emailControllerRef = doc(db, "familyInfo", familyID, "controllers", userEmail);
        await deleteDoc(emailControllerRef).catch(() => {}); // Ignore if doesn't exist
      }

      // Reload both invites and associated families
      await Promise.all([
        loadFamilyInvites(),
        loadAssociatedFamilies(accountUUID)
      ]);
    } catch (error) {
      console.error("Error accepting family invite:", error);
      setErrorMessage('Failed to accept family invite. Please try again.');
      setShowErrorModal(true);
    }
  };

  const declineFamilyInvite = async (familyID) => {
    const db = getFirestore(app);
    const auth = getAuth(app);
    try {
      // Remove invite
      const inviteMessageRef = doc(db, "familyInviteMessages", userEmail, "familyIDs", familyID);
      await deleteDoc(inviteMessageRef);

      // Check if there are any remaining invites
      const remainingInvites = await getDocs(collection(db, "familyInviteMessages", userEmail, "familyIDs"));
      if (remainingInvites.empty) {
        // Delete the parent document if no more invites exist
        await deleteDoc(doc(db, "familyInviteMessages", userEmail));
      }

      // Remove from pending invites
      const pendingInvitesRef = doc(db, "familyInfo", familyID, "pendingControllerInvites", userEmail);
      await deleteDoc(pendingInvitesRef);

      // Remove from controllers (both email-based and UUID-based)
      const emailControllersRef = doc(db, "familyInfo", familyID, "controllers", userEmail);
      await deleteDoc(emailControllersRef).catch(() => {}); // Ignore if doesn't exist

      if (accountUUID) {
        const uuidControllersRef = doc(db, "familyInfo", familyID, "controllers", accountUUID);
        await deleteDoc(uuidControllersRef).catch(() => {}); // Ignore if doesn't exist

        // Remove familyID from the 'families' array in accountsInfo
        const accountDocRef = doc(db, 'accountsInfo', accountUUID);
        await updateDoc(accountDocRef, {
          families: arrayRemove(familyID)
        });

        // Check if the 'families' array is empty
        const accountDocSnap = await getDoc(accountDocRef);
        if (accountDocSnap.exists() && (accountDocSnap.data().families || []).length === 0) {
          // Delete the account document
          await deleteDoc(accountDocRef);

          // Delete the user from Firebase Authentication
          const user = auth.currentUser;
          if (user) {
            await user.delete();
            window.location.reload();
          }
        } else {
          // Reload invites
          loadFamilyInvites();
        }
      }
    } catch (error) {
      console.error("Error declining family invite:", error);
      setErrorMessage('Failed to decline family invite. Please try again.');
      setShowErrorModal(true);
    }
  };

  const handleSettingsAvailability = useCallback((familyID, hasSettings) => {
    setFamiliesWithSettings(prev => {
      if (prev[familyID] === hasSettings) return prev;
      return { ...prev, [familyID]: hasSettings };
    });
  }, []);

  const anyFamilyHasSettings = Object.values(familiesWithSettings).some(Boolean);

  const confirmSignoutDevice = (familyID) => {
    setSelectedFamilyForSignout(familyID);
    setShowSignoutModal(true);
  };

  const handleSignoutDevice = async () => {
    if (!selectedFamilyForSignout) return;

    const db = getFirestore(app);
    // const auth = getAuth(app);
    // const user = auth.currentUser;
    // console.log("user.email: ", user.email);
    setIsSigningOut(true);
    try {
      await updateDoc(doc(db, 'familyInfo', selectedFamilyForSignout), {
        signoutRequested: true
      });
      setShowSignoutModal(false);
      setSelectedFamilyForSignout(null);
    } catch (err) {
      console.error('Error signing out device:', err);
      setErrorMessage('Failed to sign out device');
      setShowErrorModal(true);
    } finally {
      setIsSigningOut(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen w-full flex-col items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen w-full flex-col items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-red-600">Error</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-3">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="min-w-0 flex-1 pl-2">
              <h1 className="text-2xl font-bold text-slate-900">Higlo Dashboard</h1>
              <p className="text-slate-600">Setup and Configure your Hearths and Spaces</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <Button variant="ghost" size="sm" onClick={handleSignOut} className="flex items-center gap-1 text-slate-500 hover:text-slate-900 h-auto py-0.5 px-1.5">
              <LogOut className="h-3.5 w-3.5" />
              <span className="text-xs">Sign Out</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSupportChat(true)}
              className="flex items-center gap-1.5 relative"
            >
              <MessageCircle className="h-4 w-4" />
              <span className="text-xs sm:text-sm">Feedback/Help</span>
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs font-medium rounded-full h-5 w-5 flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDocs(true)}
              className="flex items-center gap-1.5"
            >
              <BookOpen className="h-4 w-4" />
              <span className="text-xs sm:text-sm">Knowledge Base</span>
            </Button>
          </div>
        </div>

        {/* Email display - moved below header row */}
        {/* <div className="hidden sm:flex items-center justify-end -mt-4 pr-2">
          <p className="text-sm text-slate-500 flex items-center gap-1">
            <Mail className="h-3.5 w-3.5" />
            <span className="truncate">{userEmail}</span>
          </p>
        </div> */}

        {/* Family Management Section */}
        <Card className="w-full">
          <CardHeader className="p-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Home className="h-6 w-6" />
                Your Hearths
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={onConfigureHiglos}
                className="flex items-center gap-1.5"
              >
                <LayoutGrid className="h-4 w-4" />
                <span className="text-xs sm:text-sm">Configure Higlos</span>
              </Button>
            </div>
            <CardDescription>
              The Primary Hearth is the Hearth in your home.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 p-3">
            {(associatedFamilies.length === 0 && familyInvites.length === 0) ? (
              <p className="text-center text-muted-foreground">No Hearths found. Add a new one to get started!</p>
            ) : (
              <div className="space-y-4">
                {/* All Families with proper indexing */}
                {associatedFamilies.map((familyID, index) => (
                  <FamilyRow
                    key={familyID}
                    familyID={familyID}
                    isPrimary={familyID === primaryFamilyID}
                    isOwned={owningFamilies.includes(familyID)}
                    index={index + 1}
                    onManage={onManageFamily}
                    onMakePrimary={handleMakePrimary}
                    onSetupDeviceClick={onSetupDevice}
                    onSignoutDevice={confirmSignoutDevice}
                    onDeviceSettings={onDeviceSettings}
                    showSettingsButton={anyFamilyHasSettings}
                    onSettingsAvailability={handleSettingsAvailability}
                    associatedFamilies={associatedFamilies}
                  />
                ))}
                
                {/* Family Invites */}
                {familyInvites.map(invite => (
                  <InviteRow
                    key={invite.familyID}
                    familyID={invite.familyID}
                    onAccept={acceptFamilyInvite}
                    onDecline={declineFamilyInvite}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Signout Device Confirmation Modal */}
        {showSignoutModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-md">
              <CardHeader className="pb-2">
                <CardTitle>Confirm Factory Reset</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-3">
                  Are you sure you want to factory reset this device?
                </p>
                <p className="text-gray-600 mb-3">
                  This action will sign out this Hearth device from your Higlo account and take you back to the Setup Device page on your TV.
                </p>
                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={() => setShowSignoutModal(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    className="bg-gray-800 text-white hover:bg-gray-700 flex items-center gap-1"
                    onClick={handleSignoutDevice}
                    disabled={isSigningOut}
                  >
                    {isSigningOut ? 'Signing Out...' : 'Continue'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Error Modal */}
        <ErrorModal
          isOpen={showErrorModal}
          message={errorMessage}
          onClose={() => {
            setShowErrorModal(false);
            setErrorMessage('');
          }}
        />

        {/* Documentation */}
        {showDocs && (
          <DocumentationViewer onClose={() => setShowDocs(false)} />
        )}

        {/* Support Chat */}
        {showSupportChat && (
          <SupportChat
            onClose={() => {
              setShowSupportChat(false);
              fetchUnreadCount();
            }}
          />
        )}
      </div>
    </div>
  );
}

function WifiSignalBars({ rssiDbm }) {
  let bars;
  let color;
  if (rssiDbm > -50) {
    bars = 4; color = '#22c55e';
  } else if (rssiDbm > -60) {
    bars = 3; color = '#22c55e';
  } else if (rssiDbm > -70) {
    bars = 2; color = '#f97316';
  } else {
    bars = 1; color = '#ef4444';
  }

  const barWidth = 2.5;
  const gap = 1.5;
  const heights = [4, 7, 10, 13];
  const totalWidth = 4 * barWidth + 3 * gap;

  return (
    <svg
      width={totalWidth}
      height={14}
      viewBox={`0 0 ${totalWidth} 14`}
      className="flex-shrink-0"
      title={`WiFi RSSI: ${Math.round(rssiDbm)} dBm`}
    >
      {heights.map((h, i) => (
        <rect
          key={i}
          x={i * (barWidth + gap)}
          y={14 - h}
          width={barWidth}
          height={h}
          rx={0.5}
          fill={i < bars ? color : '#d1d5db'}
        />
      ))}
    </svg>
  );
}

function FamilyRow({ familyID, isPrimary, isOwned, index, onManage, onMakePrimary, onSetupDeviceClick, onSignoutDevice, onDeviceSettings, showSettingsButton, onSettingsAvailability, associatedFamilies }) {
  const [familyName, setFamilyName] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editedFamilyName, setEditedFamilyName] = useState("");
  const [localShowErrorModal, setLocalShowErrorModal] = useState(false);
  const [localErrorMessage, setLocalErrorMessage] = useState("");
  const [resetEditOnClose, setResetEditOnClose] = useState(false);

  const [deviceStatus, setDeviceStatus] = useState({ isOnline: false, hasPinged: false, rssi: null, location: null, currentAppVersion: null, targetAppVersion: null, instantUpdate: false });
  const [hasApplicableSettings, setHasApplicableSettings] = useState(false);

  const [autoMode, setAutoMode] = useState(false);
  const [showAutoModeModal, setShowAutoModeModal] = useState(false);
  const [hasNonCentreIgloos, setHasNonCentreIgloos] = useState(false);
  const [isUpdatingAutoMode, setIsUpdatingAutoMode] = useState(false);

  useEffect(() => {
    const fetchFamilyData = async () => {
      const docSnap = await getDoc(doc(getFirestore(app), "familyInfo", familyID));
      if (docSnap.exists()) {
        const data = docSnap.data();
        setFamilyName(data.name || familyID);
        setAutoMode(data.autoMode || false);
      }

      // Check for non-centre igloos
      const igloosSnapshot = await getDocs(collection(getFirestore(app), "familyInfo", familyID, "igloos"));
      const hasOtherPositions = igloosSnapshot.docs.some(doc => {
        const position = doc.data().position;
        return position && position !== "Centre";
      });
      setHasNonCentreIgloos(hasOtherPositions);
    };
    fetchFamilyData();
  }, [familyID]);

  useEffect(() => {
    const firestore = getFirestore(app);
    let lastOnlinePing = null;
    let hasPinged = false;
    let rssi = null;
    let location = null;
    let currentAppVersion = null;
    let targetAppVersion = null;
    let instantUpdate = false;

    const updateStatus = () => {
      const isOnline = lastOnlinePing && (Date.now() - lastOnlinePing < 30000);
      setDeviceStatus({ isOnline, hasPinged, rssi, location, currentAppVersion, targetAppVersion, instantUpdate });
    };

    const unsubPublished = onSnapshot(
      doc(firestore, 'deviceManagement', familyID, 'status', 'everyFamilyAndAccountVisible'),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          const ping = data.onlinePing;
          if (ping) {
            lastOnlinePing = ping.toMillis ? ping.toMillis() : (ping.seconds ? ping.seconds * 1000 : ping);
            hasPinged = true;
          } else {
            lastOnlinePing = null;
          }
        } else {
          lastOnlinePing = null;
        }
        updateStatus();
      },
      () => {}
    );

    const unsubUserVisible = onSnapshot(
      doc(firestore, 'deviceManagement', familyID, 'status', 'accountVisible'),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          rssi = data.rssi ?? null;
          location = data.location ?? null;
          currentAppVersion = data.currentAppVersion ?? null;
          targetAppVersion = data.targetAppVersion ?? null;
          instantUpdate = data.instantUpdate ?? false;
        } else {
          rssi = null;
          location = null;
          currentAppVersion = null;
          targetAppVersion = null;
          instantUpdate = false;
        }
        updateStatus();
      },
      () => {}
    );

    const interval = setInterval(() => updateStatus(), 15000);

    return () => {
      unsubPublished();
      unsubUserVisible();
      clearInterval(interval);
    };
  }, [familyID]);

  const effectiveVersion = (deviceStatus.instantUpdate && deviceStatus.targetAppVersion && deviceStatus.targetAppVersion !== deviceStatus.currentAppVersion)
    ? deviceStatus.targetAppVersion
    : deviceStatus.currentAppVersion;

  useEffect(() => {
    if (!effectiveVersion) {
      setHasApplicableSettings(false);
      onSettingsAvailability(familyID, false);
      return;
    }

    const checkSettings = async () => {
      const firestore = getFirestore(app);
      const settingsDoc = await getDoc(doc(firestore, 'appUpdates', String(effectiveVersion), 'userVisible', 'userSettings'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const hasAny = Object.values(data).some(val => Array.isArray(val) && val.length > 0);
        setHasApplicableSettings(hasAny);
        onSettingsAvailability(familyID, hasAny);
      } else {
        setHasApplicableSettings(false);
        onSettingsAvailability(familyID, false);
      }
    };
    checkSettings();
  }, [effectiveVersion, familyID, onSettingsAvailability]);

  const handleEditClick = () => {
    setIsEditing(true);
    setEditedFamilyName(familyName);
  };

  const handleSaveEdit = async () => {
    const newFamilyName = (editedFamilyName || "").trim();
    if (!newFamilyName) {
      setLocalErrorMessage("The Hearth name cannot be empty.");
      setLocalShowErrorModal(true);
      setResetEditOnClose(true);
      return;
    }
    try {
      const firestore = getFirestore(app);
      // Update the family's own name
      await updateDoc(doc(firestore, "familyInfo", familyID), {
        name: newFamilyName
      });
      // Update connectedFamilies entries so all other families in the account
      // know this family by the new name
      const otherFamilies = (associatedFamilies || []).filter(id => id !== familyID);
      await Promise.all(otherFamilies.map(otherFamilyID =>
        setDoc(
          doc(firestore, "familyInfo", otherFamilyID, "connectedFamilies", familyID),
          { name: newFamilyName },
          { merge: true }
        )
      ));
      setFamilyName(newFamilyName);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating family name:", error);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedFamilyName("");
  };

  const handleSetPrimary = (checked) => {
    if (checked) {
      onMakePrimary(familyID);
    }
  };

  const handleAutoModeToggle = (checked) => {
    if (checked && hasNonCentreIgloos) {
      // Show confirmation modal if there are non-centre igloos
      setShowAutoModeModal(true);
    } else {
      // No confirmation needed, just toggle
      updateAutoMode(checked);
    }
  };

  const updateAutoMode = async (enabled) => {
    setIsUpdatingAutoMode(true);
    try {
      const db = getFirestore(app);

      if (enabled && hasNonCentreIgloos) {
        // Deactivate non-centre igloos by removing their position
        const igloosSnapshot = await getDocs(collection(db, "familyInfo", familyID, "igloos"));
        const batch = [];

        for (const iglooDoc of igloosSnapshot.docs) {
          const position = iglooDoc.data().position;
          if (position && position !== "Centre") {
            // Remove position to deactivate
            await updateDoc(doc(db, "familyInfo", familyID, "igloos", iglooDoc.id), {
              position: null
            });
            // Also remove from activeIgloos
            await deleteDoc(doc(db, "familyInfo", familyID, "activeIgloos", iglooDoc.id)).catch(() => {});
          }
        }
        setHasNonCentreIgloos(false);
      }

      // Update autoMode in familyInfo
      await updateDoc(doc(db, "familyInfo", familyID), {
        autoMode: enabled
      });

      setAutoMode(enabled);
      setShowAutoModeModal(false);
    } catch (error) {
      console.error("Error updating auto mode:", error);
      setLocalErrorMessage("Failed to update Auto Mode. Please try again.");
      setLocalShowErrorModal(true);
    } finally {
      setIsUpdatingAutoMode(false);
    }
  };

  return (
    <Card className="p-3 shadow-sm relative">
      {deviceStatus.hasPinged && (
        <div className="absolute top-2 right-3 flex items-center gap-1.5 text-xs text-muted-foreground">
          <span>
            {!deviceStatus.isOnline
              ? 'Offline'
              : deviceStatus.location === 'sleep'
                ? 'Asleep'
                : deviceStatus.location === 'training'
                  ? 'In training'
                  : deviceStatus.location === 'home' || !deviceStatus.location
                    ? 'Home'
                    : 'In Higlo'}
          </span>
          {deviceStatus.isOnline && deviceStatus.rssi != null && (
            <WifiSignalBars rssiDbm={deviceStatus.rssi} />
          )}
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              Hearth #{index}
            </span>
            {isOwned ? (
              <Badge variant="secondary" className="flex items-center gap-1 bg-slate-100 text-slate-700 text-xs">
                <Crown className="h-3 w-3" />
                Owner
              </Badge>
            ) : (
              <Badge variant="outline" className="flex items-center gap-1 text-xs">
                <UserPlus className="h-3 w-3" />
                Invited
              </Badge>
            )}
          </div>
          {isEditing ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <Input
                value={editedFamilyName}
                onChange={(e) => setEditedFamilyName(e.target.value)}
                className="text-lg font-semibold w-full sm:w-64"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveEdit();
                  if (e.key === "Escape") handleCancelEdit();
                }}
              />
              <div className="flex gap-2 justify-end sm:justify-start">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleSaveEdit}
                  aria-label="Save Family Name"
                >
                  <Check className="h-5 w-5 text-green-600" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={handleCancelEdit} 
                  aria-label="Cancel Edit"
                >
                  <X className="h-5 w-5 text-red-600" />
                </Button>
              </div>
            </div>
          ) : (
            <span
              className="text-lg font-semibold cursor-pointer hover:underline flex items-center gap-2"
              onClick={handleEditClick}
              aria-label={`Edit name of ${familyName}`}
            >
              <Home className="h-5 w-5 text-gray-600" />
              {familyName}
            </span>
          )}
          <div className="flex items-center gap-2">
            <Switch
              id={`primary-${familyID}`}
              checked={isPrimary}
              onCheckedChange={handleSetPrimary}
              aria-label={`Set ${familyName} as primary`}
            />
            <label
              htmlFor={`primary-${familyID}`}
              className="text-sm text-muted-foreground cursor-pointer"
            >
              Primary Hearth
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id={`automode-${familyID}`}
              checked={autoMode}
              onCheckedChange={handleAutoModeToggle}
              disabled={isUpdatingAutoMode}
              aria-label={`Enable Auto Mode for ${familyName}`}
            />
            <label
              htmlFor={`automode-${familyID}`}
              className="text-sm text-muted-foreground cursor-pointer flex items-center gap-1"
            >
              Auto Mode
            </label>
            <span className="relative group cursor-help text-slate-400 hover:text-slate-600">
              <Info className="h-3.5 w-3.5" />
              <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 px-2 py-1 text-xs bg-gray-900 text-white rounded shadow-lg whitespace-normal min-w-[200px] max-w-md hidden group-hover:block z-20">
                In this mode the Hearth automatically goes into the centre Higlo, and hand controls are disabled.
              </span>
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 justify-center sm:justify-end sm:ml-auto">
          <Button
            variant="default"
            size="sm"
            className="bg-gray-800 text-white hover:bg-gray-700 flex items-center gap-1 w-20 sm:w-36 text-xs sm:text-sm"
            onClick={() => onSetupDeviceClick(familyID)}
          >
            <Play className="hidden sm:flex h-4 w-4" />
            Setup
          </Button>
          {/* Per-family Configure button — disabled by default, controlled by
              ENABLE_PER_FAMILY_CONFIGURE_BUTTON at top of file. See comment there
              for full context. The onManage prop and all backend wiring remain intact. */}
          {ENABLE_PER_FAMILY_CONFIGURE_BUTTON && (
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1 w-20 sm:w-36 text-xs sm:text-sm"
              onClick={() => onManage(familyID)}
            >
              <Settings className="hidden sm:flex h-4 w-4" />
              Configure
            </Button>
          )}
          {showSettingsButton && (
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1 w-20 sm:w-36 text-xs sm:text-sm"
              onClick={() => onDeviceSettings(familyID)}
              disabled={!hasApplicableSettings}
            >
              <SlidersHorizontal className="hidden sm:flex h-4 w-4" />
              Settings
            </Button>
          )}
          {(() => {
            const isOffline = deviceStatus.hasPinged && !deviceStatus.isOnline;
            return (
              <span className="relative inline-flex group">
                <Button
                  variant="outline"
                  size="sm"
                  className={`flex items-center gap-1 w-20 sm:w-36 text-xs sm:text-sm ${isOffline ? 'opacity-50' : ''}`}
                  onClick={() => !isOffline && onSignoutDevice(familyID)}
                  disabled={isOffline}
                >
                  <Power className="hidden sm:flex h-4 w-4" />
                  <span className="sm:hidden">Reset</span>
                  <span className="hidden sm:inline">Factory Reset</span>
                </Button>
                {isOffline && (
                  <span className="absolute right-0 bottom-full mb-1 px-3 py-2 text-xs bg-gray-900 text-white rounded shadow-lg whitespace-normal w-[260px] hidden group-hover:block group-focus-within:block z-20">
                    This Hearth is offline so it can't receive the reset command. To reset, either connect to WiFi, or use hand control on the "No Network Connection" screen to reset.
                  </span>
                )}
              </span>
            );
          })()}
        </div>
      </div>
      {/* Auto Mode Confirmation Modal */}
      {showAutoModeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <CardHeader className="pb-2">
              <CardTitle>
                Enable Auto Mode
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-3">
                Auto Mode will automatically connect this Hearth to the centre Higlo when the device is opened.
              </p>
              <p className="text-gray-600 mb-3">
                <strong>Note:</strong> This Hearth has Higlos in the Left or Right positions. These will be deactivated when Auto Mode is enabled, as only the centre Higlo position is available in Auto Mode.
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setShowAutoModeModal(false)}
                  disabled={isUpdatingAutoMode}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  className="bg-gray-800 text-white hover:bg-gray-700"
                  onClick={() => updateAutoMode(true)}
                  disabled={isUpdatingAutoMode}
                >
                  {isUpdatingAutoMode ? 'Enabling...' : 'Enable Auto Mode'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Error Modal for FamilyRow */}
      <ErrorModal
        isOpen={localShowErrorModal}
        message={localErrorMessage}
        onClose={() => {
          setLocalShowErrorModal(false);
          setLocalErrorMessage("");
          if (resetEditOnClose) {
            setEditedFamilyName(familyName);
            setIsEditing(false);
            setResetEditOnClose(false);
          }
        }}
      />
    </Card>
  );
}

// New component for invite rows
function InviteRow({ familyID, onAccept, onDecline }) {
  const [familyName, setFamilyName] = useState("");

  useEffect(() => {
    const fetchFamilyName = async () => {
      try {
        const docSnap = await getDoc(doc(getFirestore(app), "familyInfo", familyID));
        if (docSnap.exists()) {
          setFamilyName(docSnap.data().name || familyID);
        }
      } catch (error) {
        console.error("Error fetching family name:", error);
      }
    };
    fetchFamilyName();
  }, [familyID]);

  return (
    <Card className="p-4 shadow-sm border-orange-200 bg-orange-50">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-xs text-orange-600 font-medium">Family Invitation</span>
          <div className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-orange-600" />
            <span className="text-lg font-semibold text-gray-900">{familyName || familyID}</span>
            <span className="text-sm text-orange-600 font-medium">(Pending Invite)</span>
          </div>
        </div>
        <div className="flex gap-2 sm:ml-auto">
          <Button
            variant="default"
            size="sm"
            className="bg-green-600 text-white hover:bg-green-700 flex items-center gap-1"
            onClick={() => onAccept(familyID)}
          >
            <Check className="h-4 w-4" />
            Accept
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-red-300 text-red-700 hover:bg-red-100 flex items-center gap-1 bg-transparent"
            onClick={() => onDecline(familyID)}
          >
            <X className="h-4 w-4" />
            Decline
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default IndividualDashboard; 
